/**
 * Slepen met de Apple Pencil (iPadOS Safari/WebKit).
 *
 * De app gebruikt HTML5 drag-and-drop (`draggable` + `onDragStart`/`onDrop`).
 * Met de pen start dat in iPadOS niet: het scrollt de lijst of doet niets. Deze
 * shim vertaalt een pen-sleep vanaf een `[draggable="true"]`-element naar
 * dezelfde gebeurtenissen (dragstart → dragenter/dragover → drop → dragend), met
 * een eigen dataTransfer, zodat bestaande handlers (palet → canvas, explorer →
 * canvas/map) ongewijzigd werken. Alleen `pointerType === "pen"`; muis en vinger
 * houden hun eigen gedrag.
 */

interface FakeDataTransfer {
  data: Map<string, string>;
  effectAllowed: string;
  dropEffect: string;
  readonly types: string[];
  readonly files: never[];
  readonly items: never[];
  setData(type: string, value: string): void;
  getData(type: string): string;
  clearData(type?: string): void;
  setDragImage(): void;
}

function fakeDataTransfer(): FakeDataTransfer {
  const data = new Map<string, string>();
  return {
    data,
    effectAllowed: "all",
    dropEffect: "move",
    get types() {
      return [...data.keys()];
    },
    files: [],
    items: [],
    setData: (type, value) => {
      data.set(type, value);
    },
    getData: (type) => data.get(type) ?? "",
    clearData: (type) => {
      if (type) data.delete(type);
      else data.clear();
    },
    setDragImage: () => {},
  };
}

function fire(
  target: Element,
  type: string,
  dt: FakeDataTransfer,
  x: number,
  y: number,
): MouseEvent {
  const ev = new MouseEvent(type, {
    bubbles: true,
    cancelable: true,
    clientX: x,
    clientY: y,
    view: window,
  });
  Object.defineProperty(ev, "dataTransfer", { value: dt });
  // Met de pen landt de linkerbovenhoek op de pentip (zie Canvas.onDrop).
  Object.defineProperty(ev, "opPenTip", { value: true });
  target.dispatchEvent(ev);
  return ev;
}

const DREMPEL = 6; // px beweging voordat het een sleep is

export function installPenDrag(): () => void {
  let cand: { el: HTMLElement; target: Element; x: number; y: number; id: number } | null = null;
  let drag: { el: HTMLElement; dt: FakeDataTransfer; ghost: HTMLElement; id: number } | null = null;
  let lastOver: Element | null = null;

  const opruimen = () => {
    window.removeEventListener("touchmove", onTouchMove, true);
    drag?.ghost.remove();
    cand = null;
    drag = null;
    lastOver = null;
  };

  const onDown = (e: PointerEvent) => {
    if (e.pointerType !== "pen") return;
    const t = e.target as Element | null;
    const el = t?.closest<HTMLElement>('[draggable="true"]');
    if (!el || t?.closest("input, textarea, [contenteditable='true']")) return;
    cand = { el, target: t as Element, x: e.clientX, y: e.clientY, id: e.pointerId };
  };

  const onMove = (e: PointerEvent) => {
    if (e.pointerType !== "pen") return;
    if (!drag && cand && e.pointerId === cand.id) {
      if (Math.hypot(e.clientX - cand.x, e.clientY - cand.y) < DREMPEL) return;
      const dt = fakeDataTransfer();
      fire(cand.el, "dragstart", dt, cand.x, cand.y);
      if (dt.data.size === 0) {
        cand = null; // handler gaf niets mee: niet sleepbaar
        return;
      }
      const ghost = document.createElement("div");
      ghost.textContent = (cand.el.textContent ?? "").trim().slice(0, 40);
      ghost.style.cssText =
        "position:fixed;z-index:2147483647;pointer-events:none;padding:4px 10px;border-radius:6px;" +
        "background:#1e293b;color:#fff;font:13px system-ui;opacity:.9;box-shadow:0 2px 8px #0006;" +
        "transform:translate(12px,12px);left:0;top:0";
      document.body.appendChild(ghost);
      // Alleen tijdens een sleep: een niet-passieve touchmove op window vertraagt anders alle scroll.
      window.addEventListener("touchmove", onTouchMove, { capture: true, passive: false });
      drag = { el: cand.el, dt, ghost, id: cand.id };
    }
    if (!drag || e.pointerId !== drag.id) return;
    e.preventDefault();
    drag.ghost.style.left = `${e.clientX}px`;
    drag.ghost.style.top = `${e.clientY}px`;
    const over = document.elementFromPoint(e.clientX, e.clientY);
    if (!over) return;
    if (over !== lastOver) {
      fire(over, "dragenter", drag.dt, e.clientX, e.clientY);
      lastOver = over;
    }
    fire(over, "dragover", drag.dt, e.clientX, e.clientY);
  };

  const onUp = (e: PointerEvent) => {
    if (e.pointerType !== "pen") return;
    if (drag && e.pointerId === drag.id) {
      const d = drag;
      d.ghost.style.display = "none";
      const over = document.elementFromPoint(e.clientX, e.clientY);
      if (over) {
        const ov = fire(over, "dragover", d.dt, e.clientX, e.clientY);
        if (ov.defaultPrevented) fire(over, "drop", d.dt, e.clientX, e.clientY);
      }
      fire(d.el, "dragend", d.dt, e.clientX, e.clientY);
      // De click die hierna volgt is geen tik op het element.
      const stop = (c: Event) => c.stopPropagation();
      window.addEventListener("click", stop, { capture: true, once: true });
      window.setTimeout(() => window.removeEventListener("click", stop, true), 400);
    }
    // Gewone tik: door onze touchstart-preventDefault komt er geen click; geef die zelf.
    if (!drag && cand && e.pointerId === cand.id && e.type === "pointerup") {
      cand.target.dispatchEvent(
        new MouseEvent("click", {
          bubbles: true,
          cancelable: true,
          clientX: e.clientX,
          clientY: e.clientY,
          view: window,
        }),
      );
    }
    opruimen();
  };

  // Voorkom dat de lijst meescrollt zodra de sleep loopt.
  const onTouchMove = (e: TouchEvent) => {
    if (drag && e.cancelable) e.preventDefault();
  };
  // En dat iOS zelf een eigen sleep/scroll start op een draggable bij pen.
  const onTouchStart = (e: TouchEvent) => {
    const touch = e.touches[0] as (Touch & { touchType?: string }) | undefined;
    if (touch?.touchType !== "stylus") return;
    const el = (e.target as Element | null)?.closest('[draggable="true"]');
    if (!el || (e.target as Element).closest("input, textarea, [contenteditable='true']")) return;
    if (e.cancelable) e.preventDefault();
  };

  window.addEventListener("pointerdown", onDown, true);
  window.addEventListener("pointermove", onMove, true);
  window.addEventListener("pointerup", onUp, true);
  window.addEventListener("pointercancel", opruimen, true);
  window.addEventListener("touchstart", onTouchStart, { capture: true, passive: false });
  return () => {
    window.removeEventListener("pointerdown", onDown, true);
    window.removeEventListener("pointermove", onMove, true);
    window.removeEventListener("pointerup", onUp, true);
    window.removeEventListener("pointercancel", opruimen, true);
    window.removeEventListener("touchstart", onTouchStart, true);
    opruimen();
  };
}
